import {
  type Asset,
  ENHANCE_MODELS,
  enhanceActionLabel,
  enhanceRequestOutputSize,
  IMAGE_SIZES,
  type Job,
  SPEECH_LANGUAGE_LABELS,
  SPEECH_SPEAKER_LABELS,
  type SpeechRequest,
  VIDEO_SIZES,
  type Voice,
} from "@studio/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Gem, ImageUpscale, MoreHorizontal, Pause, Pencil, Play, RotateCcw, Sparkles, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  canRedoAtFullQuality,
  editJob,
  enhanceResult,
  moreLikeThis,
  redoAtFullQuality,
  runAgain,
} from "@/lib/actions";
import { api } from "@/lib/api";
import { useLive } from "@/lib/events";
import { cn, formatClock, formatDuration, formatWait } from "@/lib/format";
import { jobPhase, jobTitle } from "./GpuStatus";
import { MediaThumb } from "./MediaThumb";
import { secondsLabel } from "./PromptDock";
import { Button } from "./ui";

/** Width and height of a job's frames (music and speech have none). */
function sizeOf(job: Job): { width: number; height: number } | null {
  const r = job.request;
  if (r.type === "image")
    return r.width && r.height ? { width: r.width, height: r.height } : IMAGE_SIZES[r.aspect];
  if (r.type === "video") return VIDEO_SIZES[r.resolution][r.aspect];
  if (r.type === "enhance" && r.sourceWidth && r.sourceHeight) return enhanceRequestOutputSize(r);
  return null;
}

function slotsOf(job: Job): number {
  return job.request.type === "image" ? job.request.count : 1;
}

function promptOf(job: Job): string {
  const r = job.request;
  if (r.type === "music") return r.style;
  if (r.type === "speech") return r.text;
  // An enhancement was not asked for in words: say what it does to the file.
  if (r.type === "enhance")
    return `${enhanceActionLabel(r.model, r.scale)} with ${ENHANCE_MODELS[r.model].label}`;
  return r.prompt;
}

/** Whose voice said it: the packaged speaker, the saved voice by name, or how the voice was made. */
function voiceOf(r: SpeechRequest, voices: Voice[] | undefined): string {
  if (r.mode === "speaker") return SPEECH_SPEAKER_LABELS[r.speaker].title;
  if (r.mode === "design") return "Designed voice";
  return voices?.find((v) => v.id === r.voiceId)?.name ?? "Cloned voice";
}

function specOf(job: Job, voices: Voice[] | undefined): string {
  const r = job.request;
  if (r.type === "image") return `${r.aspect} · ${r.count}×`;
  if (r.type === "video")
    return `${r.resolution === "2k" ? "2K" : r.resolution} · ${r.aspect} · ${secondsLabel(r.frames)}`;
  if (r.type === "speech")
    return `${voiceOf(r, voices)}${r.language === "auto" ? "" : ` · ${SPEECH_LANGUAGE_LABELS[r.language]}`}`;
  if (r.type === "enhance") {
    const out = sizeOf(job);
    // a video made by the studio is enhanced from the model's own output, which can be larger than what plays
    const from = `${r.sourceOriginalWidth ?? r.sourceWidth}×${r.sourceOriginalHeight ?? r.sourceHeight}`;
    const size = ENHANCE_MODELS[r.model].hdr
      ? `${from} → HDR`
      : out
        ? `${from} → ${out.width}×${out.height}`
        : `${r.scale}×`;
    return r.sourceDurationSec ? `${size} · ${formatDuration(r.sourceDurationSec)}` : size;
  }
  return `${r.durationSec < 60 ? `${r.durationSec} s` : `${Math.round(r.durationSec / 6) / 10} min`}${r.instrumental ? " · instrumental" : ""}`;
}

/** A small mark on a result the step cache made: it skipped steps, so fine detail may be a little softer. */
export function FastLabel({ job }: { job: Job }) {
  if (job.ranSpeed !== "fast") return null;
  return (
    <span
      title="Made fast: about half the steps were skipped, so fine detail can be a little softer"
      className="rounded-[5px] border border-line px-1 text-[11px] font-medium text-mute"
    >
      fast
    </span>
  );
}

export function ResultAsset({ id, compact }: { id: string; compact?: boolean }) {
  const asset = useQuery({ queryKey: ["asset", id], queryFn: () => api.asset(id) });
  if (!asset.data) return <div className="size-full animate-pulse rounded-[var(--radius-media)] bg-panel" />;
  if (asset.data.kind === "audio") return <AudioResult asset={asset.data} />;
  return (
    <Link
      to="."
      search={(prev: Record<string, unknown>) => ({ ...prev, asset: id })}
      aria-label="Open"
      className="block size-full rounded-[var(--radius-media)] outline-none focus-visible:ring-2 focus-visible:ring-film"
    >
      <MediaThumb
        asset={asset.data}
        className={cn("size-full", compact ? "" : "transition hover:brightness-110")}
        develop
        fit="cover"
      />
    </Link>
  );
}

/** A song or a line of speech has no picture: play it right in the feed, and open it for the waveform and actions. */
function AudioResult({ asset }: { asset: Asset }) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-media)] border border-line/70 bg-panel p-2 pr-3">
      <button
        type="button"
        aria-label={playing ? "Pause" : "Play"}
        onClick={() => {
          const el = audio.current;
          if (!el) return;
          if (el.paused) void el.play();
          else el.pause();
        }}
        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-film text-film-ink hover:bg-[#f7cf4f]"
      >
        {playing ? (
          <Pause className="size-5" aria-hidden />
        ) : (
          <Play className="size-5 translate-x-px" aria-hidden />
        )}
      </button>
      <Link
        to="."
        search={(prev: Record<string, unknown>) => ({ ...prev, asset: asset.id })}
        aria-label="Open"
        className="block h-11 min-w-0 flex-1 overflow-hidden rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-film"
      >
        <MediaThumb asset={asset} edge={false} className="size-full" />
      </Link>
      <span className="tabular shrink-0 text-[13px] text-mute">{formatDuration(asset.durationSec)}</span>
      {/* biome-ignore lint/a11y/useMediaCaption: generated audio has no captions */}
      <audio
        ref={audio}
        src={asset.url}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}

/** The cell every run's frames fit into, the same for all runs so the text never shifts: phones, then wide screens. */
const PHONE_CELL = { width: 112, height: 84 };
const WIDE_CELL = { width: 224, height: 126 };
const CELL_GAP = 4;

/** One frame's size inside a cell: as large as fits, keeping its shape. */
function fitInCell(cell: { width: number; height: number }, ratio: number, count: number) {
  const width = Math.min(cell.height * ratio, (cell.width - CELL_GAP * (count - 1)) / count);
  return { width: Math.floor(width), height: Math.floor(width / ratio) };
}

/** Height of one frame where frames sit under the text (chat). */
function frameHeight(count: number): string {
  if (count >= 3) return "clamp(96px, 16dvh, 124px)";
  if (count === 2) return "clamp(110px, 19dvh, 150px)";
  return "clamp(120px, 23dvh, 184px)";
}

/**
 * One run on the contact sheet: small frames (or frame-shaped placeholders that say where the job stands) in a fixed
 * cell beside what was asked, on phones too. With layout="stack" (chat) the frames sit under the text.
 */
export function RunCard({ job: initial, layout = "sheet" }: { job: Job; layout?: "sheet" | "stack" }) {
  const job = useLive((s) => s.jobs[initial.id]) ?? initial;
  const queryClient = useQueryClient();
  // Only a cloned voice has a name to look up; every other kind of job says who spoke from the request alone.
  const voices = useQuery({
    queryKey: ["voices"],
    queryFn: api.voices,
    staleTime: 60_000,
    enabled: job.request.type === "speech" && job.request.mode === "clone",
  });
  const size = sizeOf(job);
  const pending = job.status === "queued" || job.status === "loading_model" || job.status === "running";
  const count = slotsOf(job);
  const ids = job.status === "completed" ? job.resultAssetIds : Array.from({ length: count }, () => null);
  const sheet = layout === "sheet" && size !== null;

  const frames = size ? (
    <div
      className={cn(
        "flex min-w-0",
        sheet
          ? "h-[var(--cell-h-sm)] w-[var(--cell-w-sm)] items-center justify-center gap-1 rounded-[var(--radius-media)] bg-raised/50 md:h-[var(--cell-h)] md:w-[var(--cell-w)]"
          : "gap-1.5",
      )}
      style={{
        ["--frame-h" as string]: frameHeight(ids.length),
        ["--cell-w-sm" as string]: `${PHONE_CELL.width}px`,
        ["--cell-h-sm" as string]: `${PHONE_CELL.height}px`,
        ["--cell-w" as string]: `${WIDE_CELL.width}px`,
        ["--cell-h" as string]: `${WIDE_CELL.height}px`,
      }}
    >
      {ids.map((id, i) => {
        const ratio = size.width / size.height;
        const phone = fitInCell(PHONE_CELL, ratio, ids.length);
        const wide = fitInCell(WIDE_CELL, ratio, ids.length);
        return (
          <div
            key={id ?? `frame-${i}`}
            className={cn(
              "relative flex min-w-0 overflow-hidden rounded-[var(--radius-media)]",
              sheet
                ? "h-[var(--fit-h-sm)] w-[var(--fit-w-sm)] shrink-0 md:h-[var(--fit-h)] md:w-[var(--fit-w)]"
                : "shrink grow-0 basis-[calc(var(--frame-h)*var(--ratio))]",
              // a frame too narrow for its edge code (a portrait picture in the phone cell) shows the picture alone
              sheet && phone.width < 64 && "max-md:[&_[data-edge]]:hidden",
              id === null && "border border-line/70 bg-panel",
              id === null && job.status === "failed" && "border-danger/40",
            )}
            style={{
              aspectRatio: `${size.width} / ${size.height}`,
              ["--ratio" as string]: ratio,
              ["--fit-w-sm" as string]: `${phone.width}px`,
              ["--fit-h-sm" as string]: `${phone.height}px`,
              ["--fit-w" as string]: `${wide.width}px`,
              ["--fit-h" as string]: `${wide.height}px`,
            }}
          >
            {id !== null ? (
              <ResultAsset id={id} />
            ) : (
              <>
                {pending ? (
                  <span className="animate-scan absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-film/10 to-transparent" />
                ) : null}
                {i === 0 ? (
                  <p
                    className={cn(
                      "relative m-auto px-1.5 text-center text-[11px] font-medium leading-4 md:px-2 md:text-[12px]",
                      job.status === "failed" ? "text-danger" : "text-paper",
                    )}
                  >
                    {job.status === "failed" ? "Did not work" : jobPhase(job)}
                  </p>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </div>
  ) : job.status === "completed" ? (
    <div className="flex max-w-[560px] flex-col gap-2">
      {job.resultAssetIds.map((id) => (
        <ResultAsset key={id} id={id} />
      ))}
    </div>
  ) : (
    <div
      className={cn(
        "relative flex h-14 max-w-[560px] items-center overflow-hidden rounded-[var(--radius-media)] border border-line/70 bg-panel px-4",
        job.status === "failed" && "border-danger/40",
      )}
    >
      {pending ? (
        <span className="animate-scan absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-film/10 to-transparent" />
      ) : null}
      <p
        className={cn(
          "relative text-[13px] font-medium",
          job.status === "failed" ? "text-danger" : "text-paper",
        )}
      >
        {job.status === "failed" ? "Did not work" : jobPhase(job)}
      </p>
    </div>
  );

  const status =
    job.status === "failed"
      ? job.error
      : job.status === "draft"
        ? "Check the plan, then start it."
        : pending && job.etaSec
          ? `About ${formatWait(job.etaSec)} left`
          : null;

  return (
    <article
      className={cn(
        "border-b border-line/50 py-3 last:border-b-0 md:py-4",
        sheet && "flex items-start gap-3 md:gap-4",
      )}
    >
      {sheet ? <div className="shrink-0">{frames}</div> : null}
      <div className="min-w-0 flex-1">
        <p
          className="line-clamp-2 text-[14px] leading-5 text-paper md:text-[15px] md:leading-6"
          title={promptOf(job)}
        >
          {promptOf(job)}
        </p>
        <p className="tabular mt-0.5 line-clamp-2 text-[12px] text-mute md:text-[13px]">
          {jobTitle(job)} · {specOf(job, voices.data)} · {formatClock(job.createdAt)}
          {job.ranSpeed === "fast" ? (
            <>
              {" "}
              <FastLabel job={job} />
            </>
          ) : null}
          {job.chatId ? (
            <>
              {" · "}
              <Link
                to="/chat/$chatId"
                params={{ chatId: job.chatId }}
                className="underline-offset-2 hover:underline"
              >
                from chat
              </Link>
            </>
          ) : null}
        </p>
        {status ? (
          <p
            className={cn(
              "tabular mt-1 line-clamp-3 text-[13px]",
              job.status === "failed" ? "text-danger" : "text-mute",
            )}
          >
            {status}
          </p>
        ) : null}
        {!sheet ? <div className="mt-2.5">{frames}</div> : null}
        <Actions job={job} queryClient={queryClient} iconsOnPhone={sheet} />
      </div>
    </article>
  );
}

function Actions({
  job,
  queryClient,
  iconsOnPhone,
}: {
  job: Job;
  queryClient: ReturnType<typeof useQueryClient>;
  iconsOnPhone: boolean;
}) {
  const jobs = useLive((s) => s.jobs);
  // beside a small frame on a phone there is room for icons; the words stay for screen readers and wide screens
  const word = iconsOnPhone ? "max-md:sr-only" : undefined;
  const icon = iconsOnPhone ? "max-md:px-2.5" : undefined;
  return (
    <div className="-ml-2 mt-1 flex flex-wrap items-center gap-0.5 md:mt-1.5">
      {job.status === "draft" ? (
        <>
          <Button
            size="sm"
            variant="primary"
            onClick={async () => {
              try {
                useLive.getState().upsertJob(await api.startJob(job.id));
              } catch (error) {
                toast.error((error as Error).message);
              }
            }}
            className="ml-2"
          >
            Start
          </Button>
          <Button
            size="sm"
            variant="quiet"
            className={icon}
            title="Edit"
            onClick={() => editJob(job, queryClient)}
          >
            <Pencil className="size-4" aria-hidden />
            <span className={word}>Edit</span>
          </Button>
          <Button
            size="sm"
            variant="quiet"
            className={icon}
            title="Discard"
            onClick={() => api.cancelJob(job.id).then((j) => useLive.getState().upsertJob(j))}
          >
            <X className={cn("size-4", !iconsOnPhone && "hidden")} aria-hidden />
            <span className={word}>Discard</span>
          </Button>
        </>
      ) : null}
      {job.status === "completed" ? (
        <>
          {/* the same file through the same model comes out the same: nothing "like this" to make */}
          {job.type !== "enhance" ? (
            <Button
              size="sm"
              variant="quiet"
              className={icon}
              title="More like this"
              onClick={() => moreLikeThis(job)}
            >
              <Sparkles className="size-4" aria-hidden />
              <span className={word}>More like this</span>
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="quiet"
            className={icon}
            title="Edit"
            onClick={() => editJob(job, queryClient)}
          >
            <Pencil className="size-4" aria-hidden />
            <span className={word}>Edit</span>
          </Button>
          {canRedoAtFullQuality(job, jobs) ? (
            <Button
              size="sm"
              variant="quiet"
              className={icon}
              title="Redo at full quality: the same settings and seed, every step"
              onClick={() => redoAtFullQuality(job)}
            >
              <Gem className="size-4" aria-hidden />
              <span className={word}>Redo at full quality</span>
            </Button>
          ) : null}
          {(job.type === "image" || job.type === "video") && job.resultAssetIds.length === 1 ? (
            <Button
              size="sm"
              variant="quiet"
              className={icon}
              title="Enhance: restore and enlarge it"
              onClick={() => enhanceResult(job, queryClient)}
            >
              <ImageUpscale className="size-4" aria-hidden />
              <span className={word}>Enhance</span>
            </Button>
          ) : null}
        </>
      ) : null}
      {job.status === "failed" || job.status === "cancelled" ? (
        <>
          <Button size="sm" variant="quiet" className={icon} title="Try again" onClick={() => runAgain(job)}>
            <RotateCcw className="size-4" aria-hidden />
            <span className={word}>Try again</span>
          </Button>
          <Button
            size="sm"
            variant="quiet"
            className={icon}
            title="Edit"
            onClick={() => editJob(job, queryClient)}
          >
            <Pencil className="size-4" aria-hidden />
            <span className={word}>Edit</span>
          </Button>
        </>
      ) : null}
      {job.status === "queued" ? (
        <Button
          size="sm"
          variant="quiet"
          onClick={() => api.cancelJob(job.id).then((j) => useLive.getState().upsertJob(j))}
        >
          Cancel
        </Button>
      ) : null}
      {job.status === "completed" && job.resultAssetIds.length === 1 ? (
        <Link
          to="."
          search={(prev: Record<string, unknown>) => ({ ...prev, asset: job.resultAssetIds[0] })}
          className="ml-auto inline-flex size-9 items-center justify-center rounded-full text-mute hover:bg-raised hover:text-paper"
          aria-label="Open"
        >
          <MoreHorizontal className="size-5" />
        </Link>
      ) : null}
    </div>
  );
}
